import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import { skipToken, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { ResultOf } from 'gql.tada'
import * as _ from 'lodash-es'

import { always_default_data } from '@repo/common/helpers/query_helpers'
import { graphql_call } from '@repo/common/queries/schemas/server_user_query'
import { graphql_call as graphql_prices_call } from '@repo/common/queries/schemas/subgraph_prices_query'

import { disableKeyWhenMissing } from '../helpers/utilities'
import { useGetTokenAddress } from '../products'
import {
  getTokenNames,
  type TokenName,
  type VaultSymbol,
} from '../products/products_names'

import { query_list_vaults } from './vaults_graphql'
import {
  query_get_daily_price,
  query_get_oldest_price,
} from './vaults_price_graphql'

export const keys = createQueryKeyStore({
  vaults: {
    all: null,
    details: ({ symbol }: { symbol: string }) => ({
      queryKey: [{ symbol }],
      contextQueries: {
        // blockchain
        balances: null,
        exchange_rate: null,
        total_tokens: null,
        vault_paused: null,
        vault_settings: null,
        vault_auto_range: null,
        deposit_limit: null,
        deposit_tokens: (args: {
          usdc_tokens: number
          fund_tokens: number
        }) => [args],

        // graphql
        original_price: null,
        history: null,
      },
    }),
  },
})

export type VaultFindBy = {
  id?: RubyID
  symbol?: VaultSymbol
}

export type VaultType = TokenName & {
  symbol: VaultSymbol
  id: RubyID
  token_address: CryptoAddress
  vault_contract_address: CryptoAddress
  vault_contract_history: {
    address: CryptoAddress
    version: 'v3'
  }[]
}

export function useListVaults() {
  const results = useQuery<VaultType[]>({
    queryKey: keys.vaults.all.queryKey,
    queryFn: async () => {
      const data = await graphql_call({ query: query_list_vaults })
      if (data == null) return []

      return data.map((f) => ({
        ...getTokenNames(f.symbol as VaultSymbol),
        id: f.id,
        symbol: f.symbol as VaultSymbol,
        token_address: f.token_address!,
        vault_contract_address: f.vault_contract!.address,
        vault_contract_history: f.vault_contract_history,
      }))
    },
  })

  const data = results.data ?? []

  const vault_find_by = ({ id, symbol }: VaultFindBy) =>
    data.find((f) => f.id == id || f.symbol === symbol)

  return {
    ...results,
    data,
    vault_find_by,
  }
}

export function useVaultInfo(args: VaultFindBy) {
  const { vault_find_by, ...others } = useListVaults()

  return { ...others, data: vault_find_by(args) }
}

export function useGetVaultOriginalPrice({ symbol }: { symbol: VaultSymbol }) {
  const vault_token_address = useGetTokenAddress({ symbol })

  const enabled = vault_token_address != null
  return useQuery<{ price: number; date: ISO8601DateTime } | null>({
    queryKey: keys.vaults.details({ symbol: disableKeyWhenMissing(symbol) })
      ._ctx.original_price.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const result = await graphql_prices_call({
            query: query_get_oldest_price,
            variables: { vault_token_address },
          })
          if (result == null) return null
          return {
            price: Number(result[0].price_per_token),
            date: dayjs(Number(result[0].timestamp)).toISOString(),
          }
        },
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

export type VaultHistoryType = Omit<
  ResultOf<
    typeof query_get_daily_price
  >['vaultPriceDailyStats_collection'][number],
  'timestamp'
> & {
  timestamp: number
}

export function useGetVaultHistory({ symbol }: { symbol: VaultSymbol }) {
  const vault_token_address = useGetTokenAddress({ symbol })

  const enabled = vault_token_address != null

  return always_default_data(
    [],
    useQuery<VaultHistoryType[]>({
      queryKey: keys.vaults.details({ symbol })._ctx.history.queryKey,
      queryFn: !enabled
        ? skipToken
        : async () => {
            const results = await graphql_prices_call({
              query: query_get_daily_price,
              variables: { vault_token_address },
            })
            if (results == null) return []

            let corrected = results.map(({ timestamp, ...r }) => ({
              ...r,
              timestamp: Number(timestamp),
            }))

            corrected = _.sortBy(corrected, 'timestamp')
            // if today is missing add it using yesterday's price
            const last = corrected[corrected.length - 1]
            if (dayjs(last.timestamp).isBefore(dayjs(), 'day')) {
              corrected.push({
                ...last,
                timestamp: dayjs().valueOf(),
              })
            }
            return corrected
          },
      staleTime: 1000 * 60 * 60 * 24, // 1 day
      gcTime: Infinity,
    }),
  )
}
